import { Clock, Pencil } from 'lucide-react';
import { Link } from 'react-router';
import type { PaginatedResponse, User } from '@/api/admin/users';
import { DateTimeCell } from '@/components/table/cells';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface UserTableProps {
  data: PaginatedResponse<User>;
  currentPage: number;
  onPageChange: (page: number) => void;
  onOpenAuditModal: (user: { id: string; name: string }) => void;
  canEdit?: boolean;
  canViewHistory?: boolean;
}

export function UserTable({
  data,
  currentPage,
  onPageChange,
  onOpenAuditModal,
  canEdit = true,
  canViewHistory = true,
}: UserTableProps) {
  const docs = data.docs ?? [];

  return (
    <div className="w-full space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Perfis</TableHead>
              <TableHead>Data de criação</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                  Nenhum usuário encontrado
                </TableCell>
              </TableRow>
            ) : (
              docs.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{user.name}</span>
                      <span className="text-sm text-gray-500">{user.email}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.lock ? (
                      <Badge variant="destructive-subtle">Bloqueado</Badge>
                    ) : user.active ? (
                      <Badge variant="success-subtle">Ativo</Badge>
                    ) : (
                      <Badge variant="secondary-subtle">Inativo</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.profileName && <Badge variant="outline">{user.profileName}</Badge>}
                  </TableCell>
                  <TableCell>
                    <DateTimeCell date={user.created_at} />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2 items-center">
                      {canViewHistory && (
                        <Button
                          variant="outline"
                          size="icon"
                          title="Histórico"
                          onClick={() => onOpenAuditModal({ id: user.id, name: user.name })}
                          className="h-8 w-8"
                        >
                          <Clock className="h-4 w-4" />
                        </Button>
                      )}
                      {canEdit && (
                        <Button
                          variant="outline"
                          size="icon"
                          title="Editar"
                          asChild
                          className="h-8 w-8"
                        >
                          <Link to={`/admin/users/${user.id}/edit`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={data.pages}
        totalItems={data.total}
        limit={data.limit}
        onPageChange={onPageChange}
      />
    </div>
  );
}
